import { Injectable, inject } from "@angular/core";
import { HttpHelperService } from "@helpers/http";
import {
  injectQuery,
  injectMutation,
  injectQueryClient,
  toPromise,
  injectInfiniteQuery,
} from "@ngneat/query";

import { TMenuCollectionRelatedProductDTO } from "./interfaces";
import * as qs from "qs";
import { Observable, catchError, map } from "rxjs";
import { IHttpResponse } from "@helpers/http/interfaces";

import { AlertController, ToastController } from "@ionic/angular";
import { TMenuCollectionContentDTO } from "./interfaces";
@Injectable({
  providedIn: "root",
})
export class MenuCollectionApiService {
  #query = injectQuery();
  #queryInfinite = injectInfiniteQuery();

  #mutation = injectMutation();
  #client = injectQueryClient();
  #http = inject(HttpHelperService);

  private alertCtrl = inject(AlertController);
  private toastCtrl = inject(ToastController);

  getList(menuId: number, searchQuery = {}) {
    return this.#queryInfinite({
      queryKey: ["menuCollections", searchQuery],

      queryFn: ({ pageParam, signal }) => {
        const queries = qs.stringify({
          ...searchQuery,
          ...{ skip: pageParam },
        });
        const path = `admin/menu/${menuId}/collections?${queries}`;
        return toPromise({
          source: this.#http.get(path).pipe(map((res) => res.data)),
          signal,
        });
      },
      initialPageParam: 0,
      getPreviousPageParam: (firstPage) => null,
      getNextPageParam: (lastPage) => {
        if (!lastPage) return null;
        const { count, skip, limit } = lastPage;
        if (count <= skip + limit) return null;
        return lastPage.skip + lastPage.limit;
      },
    });
  }

  getById(menuId: number, id: number) {
    return this.#query({
      queryKey: ["menuCollection"] as const,
      queryFn: () => {
        const path = `admin/menu/${menuId}/collection/${id}`;
        return this.#http.get(path).pipe(map((res) => res.data));
      },
    });
  }

  addCollection<T>(
    mid: number,
    body: { data: TMenuCollectionContentDTO }
  ): Observable<IHttpResponse<T>> {
    const path = `admin/menu/${mid}/collection/add`;

    return this.#http.post(path, body).pipe(catchError(this.#http.catch()));
  }

  removeCollection() {
    return this.#mutation({
      onSuccess: () => {
        this.#client.invalidateQueries({});
        this.presentToast("Collection deleted !");
      },
      onError: this.errorHandler,
      mutationFn: ({ mid, collectionId }) => {
        const path = `admin/menu/${mid}/collection/${collectionId}`;

        return this.#http.delete(path).pipe(map((res) => res.data));
      },
    });
  }

  updateStatus<T>() {
    return this.#mutation({
      onSuccess: () => {
        this.#client.invalidateQueries({});
      },
      onError: this.errorHandler,
      mutationFn: ({ mid, collectionId, status }) => {
        const path = `admin/menu/${mid}/collection/${collectionId}/status`;
        return this.#http.put(path, { status }).pipe(map((res) => res.data));
      },
    });
  }

  updateContent<T>() {
    return this.#mutation({
      onSuccess: () => {
        this.#client.invalidateQueries({});
        this.presentToast("Content Updated !");
      },
      onError: this.errorHandler,
      mutationFn: ({ mid, collectionId, body }) => {
        const path = `admin/menu/${mid}/collection/${collectionId}/content`;
        return this.#http.put(path, body).pipe(map((res) => res.data));
      },
    });
  }

  getRelatedProductList(menuId: number, collectionId: number) {
    return this.#query({
      queryKey: ["menuCollectionRelatedProductList"] as const,
      queryFn: () => {
        const path = `admin/menu/${menuId}/collection/${collectionId}/related/products`;
        return this.#http.get(path).pipe(map((res) => res.data?.products));
      },
    });
  }

  updateProducts<T>() {
    return this.#mutation({
      onSuccess: () => {
        this.#client.invalidateQueries({
          queryKey: ["menuCollection"],
        });

        this.#client.invalidateQueries({
          queryKey: ["menuDishes"],
        });

        this.#client.invalidateQueries({
          queryKey: ["menuDish"],
        });

        this.presentToast("Dishes Updated !");
      },
      onError: this.errorHandler,
      mutationFn: ({
        mid,
        collectionId,
        body,
      }: {
        mid: number;
        collectionId: number;
        body: TMenuCollectionRelatedProductDTO;
      }) => {
        const path = `admin/menu/${mid}/collection/${collectionId}/related/products`;

        return this.#http.put(path, body).pipe(map((res) => res.data));
      },
    });
  }

  updateRelatedProductsPosition() {
    return this.#mutation({
      onSuccess: () => {
        this.#client.invalidateQueries({
          queryKey: ["menuCollectionRelatedProductList"],
        });
        this.presentToast("Product orders Updated !");
      },
      onError: this.errorHandler,
      mutationFn: ({ mid, collectionId, body }) => {
        const path = `admin/menu/${mid}/collection/${collectionId}/related/products/position`;

        return this.#http.put(path, body).pipe(map((res) => res.data));
      },
    });
  }

  errorHandler = async (error: any, variables, context) => {
    if (typeof error === "string") {
      const alert = await this.alertCtrl.create({
        header: `Error`,
        // subHeader: "Error",
        message: error,
        buttons: ["OK"],
      });

      alert.present();
    } else {
      const alert = await this.alertCtrl.create({
        header: error.name,
        subHeader: error.message,
        message: error.problems ? error.problems.join(", ") : "",
        buttons: ["OK"],
      });

      alert.present();
    }

    if (error.error instanceof ProgressEvent) {
      const alert = await this.alertCtrl.create({
        header: `ProgressEvent Error`,
        // subHeader: "Error",
        message: "Please try later",
        buttons: ["OK"],
      });

      alert.present();
    } else if (error.error) {
      const alert = await this.alertCtrl.create({
        header: error.error.name,
        subHeader: error.error.message,
        message: error.error.problems ? error.error.problems.join(", ") : "",
        buttons: ["OK"],
      });

      alert.present();
    }
  };

  async presentToast(
    message: string,
    color: "danger" | "success" | "warning" = "success"
  ) {
    const toast = await this.toastCtrl.create({
      message,
      duration: 3000,
      buttons: [
        {
          text: "Dismiss",
          role: "cancel",
          handler: () => {
            console.log("Dismiss clicked");
          },
        },
      ],
      position: "top",
      positionAnchor: "header",
      color,
      swipeGesture: "vertical",
    });

    await toast.present();
  }
}
